import api from './api';

/**
 * Service for room availability-related API calls
 */

/**
 * Fetch all room availability records
 * @returns {Promise<Array>} Array of room availability data
 */
export const fetchAllRoomAvailability = async () => {
  try {
    const response = await api.get('/room-availability');
    return response.data;
  } catch (error) {
    console.error('Error fetching room availability:', error);
    throw error;
  }
};

/**
 * Fetch room availability records for a specific room type
 * @param {string} roomTypeId - The ID of the room type
 * @returns {Promise<Array>} Array of room availability data
 */
export const fetchRoomAvailabilityByRoomType = async (roomTypeId) => {
  try {
    const response = await api.get(`/room-availability/room-type/${roomTypeId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching room availability for room type ${roomTypeId}:`, error);
    throw error;
  }
};

/**
 * Fetch room availability records for a specific date range
 * @param {string} roomTypeId - The ID of the room type
 * @param {Date} startDate - Start date for availability check
 * @param {Date} endDate - End date for availability check
 * @returns {Promise<Array>} Array of room availability data
 */
export const fetchRoomAvailabilityByDateRange = async (roomTypeId, startDate, endDate) => {
  try {
    const response = await api.get(`/room-availability/date-range/${roomTypeId}`, {
      params: {
        startDate: startDate instanceof Date ? startDate.toISOString() : startDate,
        endDate: endDate instanceof Date ? endDate.toISOString() : endDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching room availability for date range:`, error);
    throw error;
  }
};

/**
 * Check availability for a room type during a specific date range
 * @param {Object} checkAvailabilityData - Data for availability check
 * @param {string} checkAvailabilityData.roomTypeId - The ID of the room type
 * @param {Date} checkAvailabilityData.checkInDate - Check-in date
 * @param {Date} checkAvailabilityData.checkOutDate - Check-out date
 * @param {number} checkAvailabilityData.roomsNeeded - Number of rooms needed (default: 1)
 * @returns {Promise<Object>} Availability check result
 */
export const checkRoomAvailability = async (checkAvailabilityData) => {
  try {
    const response = await api.post('/room-availability/check', {
      ...checkAvailabilityData,
      checkInDate: checkAvailabilityData.checkInDate instanceof Date 
        ? checkAvailabilityData.checkInDate.toISOString() 
        : checkAvailabilityData.checkInDate,
      checkOutDate: checkAvailabilityData.checkOutDate instanceof Date 
        ? checkAvailabilityData.checkOutDate.toISOString() 
        : checkAvailabilityData.checkOutDate,
    });
    return response.data;
  } catch (error) {
    console.error('Error checking room availability:', error);
    throw error;
  }
};

/**
 * Create a bulk room availability for a date range
 * @param {Object} bulkAvailabilityData - Data for bulk availability creation
 * @param {string} bulkAvailabilityData.roomTypeId - The ID of the room type
 * @param {Date} bulkAvailabilityData.startDate - Start date for availability
 * @param {Date} bulkAvailabilityData.endDate - End date for availability
 * @param {number} bulkAvailabilityData.availableRooms - Number of available rooms
 * @param {number} bulkAvailabilityData.specialPrice - Optional special price
 * @param {boolean} bulkAvailabilityData.isClosed - Whether the room type is closed for booking
 * @returns {Promise<Array>} Created room availability records
 */
export const createBulkRoomAvailability = async (bulkAvailabilityData) => {
  try {
    const response = await api.post('/room-availability/bulk', {
      ...bulkAvailabilityData,
      startDate: bulkAvailabilityData.startDate instanceof Date 
        ? bulkAvailabilityData.startDate.toISOString() 
        : bulkAvailabilityData.startDate,
      endDate: bulkAvailabilityData.endDate instanceof Date 
        ? bulkAvailabilityData.endDate.toISOString() 
        : bulkAvailabilityData.endDate,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating bulk room availability:', error);
    throw error;
  }
};
